import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { SlickUtilsService } from "../utils/slick-utils.service";
import { SlickSleepService } from "../utils/slick-sleep.service";
import { ISlickFileModel } from "../slick-file-model/slick-file.model";
import { SlickPhotoGalleryComponent, SlickPhotoGalleryModel } from "../slick-photo-gallery/slick-photo-gallery.module";
import { SlickDialogComponent } from "../slick-dialog/slick-dialog.component";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

@Component({
	selector: 'slick-file-list',
	templateUrl: 'slick-file-list.component.html'
})
export class SlickFileListComponent implements OnChanges {
	@Input("files") files: ISlickFileModel[];
	@Input("allowDelete") allowDelete: boolean = true;
	@Input("showPhotoGallery") showPhotoGallery: boolean = false;
	@Input("showCheckboxes") showCheckboxes: boolean = false;
	@Input("thumbnailSize") thumbnailSize: number = 90;
	@Input("confirmDelete") confirmDelete: boolean = true;
	@Output("onFileChanged") onFileChanged: EventEmitter<ISlickFileModel> = new EventEmitter();
	@Output("onFileDelete") onFileDeleteEmitter: EventEmitter<ISlickFileModel> = new EventEmitter();
	@Output("onFileCheckChanged") onFileCheckChanged: EventEmitter<ISlickFileModel> = new EventEmitter();
	@Output("onFileClicked") onFileClicked: EventEmitter<ISlickFileModel> = new EventEmitter();
	@Input("allowTitleEditing") allowTitleEditing: boolean = true;

	@ViewChild("photoGalleryRef") photoGalleryRef: SlickPhotoGalleryComponent;
	@ViewChild("videoPlayerDialogRef") videoPlayerDialogRef: SlickDialogComponent;

	photos: SlickPhotoGalleryModel[] = [];
	uuid: string;
	videoSafeUrl: SafeResourceUrl;
	videoDialogHeight: number = 0;
	videoDialogWidth: number = 0;
	videoDialogTop: number = 50;
	private lastCheckedIdx: number = null;

	constructor(private sanitizer: DomSanitizer
) {
		this.uuid = SlickUtilsService.newGuid();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.thumbnailSize)
			this.thumbnailSize = parseInt(this.thumbnailSize.toString());

		if (changes.allowDelete && changes.allowDelete.currentValue)
			this.allowDelete = (changes.allowDelete.currentValue.toString().toLowerCase() !== 'false');
		
		if (changes.showPhotoGallery && changes.showPhotoGallery.currentValue)
			this.showPhotoGallery = (changes.showPhotoGallery.currentValue.toString().toLowerCase() !== 'false');

		if (changes.showCheckboxes && changes.showCheckboxes.currentValue)
			this.showCheckboxes = (changes.showCheckboxes.currentValue.toString().toLowerCase() !== 'false');

		if (changes.deleteConfirmation && changes.deleteConfirmation.currentValue)
			this.confirmDelete = (this.confirmDelete.toString() === 'true') ? true : false;
    }

    fileClicked(file: ISlickFileModel, idx: number) {
		const fileExt = (file?.fileExt || "").toLowerCase();
		const isVideo = fileExt.includes("mp4") || fileExt.includes("avi") || fileExt.includes("mov") || fileExt.includes("wmv") || fileExt.includes("flv");
		if (isVideo) {
			const screenHeight = window.innerHeight;
			const screenWidth = window.innerWidth;

			this.videoDialogHeight = Math.floor(screenHeight * 0.85);
			this.videoDialogWidth = Math.min(Math.floor(screenWidth * 0.9), 800);
			this.videoDialogTop = Math.max(40, Math.floor((screenHeight - this.videoDialogHeight) / 2));
			this.videoSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.url);
			this.videoPlayerDialogRef.showDialog();
			return;
		}
		if (this.showPhotoGallery === true) {
			const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tif', 'tiff'];
			
			const urlLooksLikeImage = imageExtensions.some(ext => (file?.url || "").toLowerCase().includes(`.${ext}`));
			const isImage = !fileExt || imageExtensions.some(ext => fileExt.includes(ext)) || urlLooksLikeImage;

			if (isImage) {
				this.photoGalleryRef.showPhotoGallery(idx);
			}
			else if (file?.url) {
				// Non-image files (PDFs, Office docs, etc.) can't render in the image gallery,
				// so open them in a new tab instead.
				window.open(file.url, '_blank');
			}
		}

		if (this.onFileClicked)
			this.onFileClicked.emit(file);
	}

	onFileChange(file: ISlickFileModel, idx: number) {
		this.files[idx] = file;
		this.onFileChanged.emit(file);
	}

	async onFileDelete(file: ISlickFileModel, idx) {
		this.files.splice(idx, 1);
		if (this.onFileDeleteEmitter)
			this.onFileDeleteEmitter.emit(file);
	}

	async onCheckChanged(e: MouseEvent, fileIdx: number) {
		await SlickSleepService.sleep();

		if (e.shiftKey === true && this.lastCheckedIdx !== null) {
			let startIdx = (this.lastCheckedIdx < fileIdx) ? this.lastCheckedIdx : fileIdx;
			let endIdx = (this.lastCheckedIdx < fileIdx) ? fileIdx : this.lastCheckedIdx;

			for (let i = startIdx; i <= endIdx; i++) {
				this.files[i].isChecked = true;
				if (this.onFileCheckChanged)
					this.onFileCheckChanged.emit(this.files[i]);
			}
		}
		else {
			this.lastCheckedIdx = fileIdx;
			if (this.onFileCheckChanged)
				this.onFileCheckChanged.emit(this.files[fileIdx]);
		}


	}

	checkAllFiles() {
		this.files.forEach(x => {
			if (x.isChecked === false && this.onFileCheckChanged) {
				x.isChecked = true;
				this.onFileCheckChanged.emit(x);
			}

			x.isChecked = true;
		});
	}

	uncheckAllFiles() {
		this.files.forEach(x => {
			if (x.isChecked === true && this.onFileCheckChanged) {
				x.isChecked = false;
				this.onFileCheckChanged.emit(x);
			}

			x.isChecked = false;
		});
	}

	getCheckedFiles(): ISlickFileModel[] {
		return this.files.filter(x => x.isChecked === true);
	}
}
